import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Row, Col } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { RootState } from '@store/index'
import Button from '@components/common/Button/Button.component'
import { Claim } from '@dataTypes/claimFeature.types'
import ClaimPostingDetails from '../ClaimPostingInfo/ClaimPostingDetails'
import ClaimMemoModal from '../ClaimMemoModal/ClaimMemoModal.component'
import './ViewClaimPostingInfo.css'
import StatusBadge, {
  CLAIMS_STATUS_STYLES,
  StatusTypes,
} from '@components/common/StatusBadge/StatusBadge.component'
import { filteredClaimsData, mapClaimStatus } from '@utils/claims.util'
import { MessageComponent } from '@syncfusion/ej2-react-notifications'
import DataTreeGridComponent from '@components/common/DataTreeGridComponent/DataTreeGridComponent'
import { getTrackingCheckStatus } from '@utils/tracking.util'

interface ViewClaimPostingInfoProps {
  isVisible: boolean
  closeModal: () => void
  claim: Claim
  claimType: string
}

const ViewClaimPostingInfo: React.FC<ViewClaimPostingInfoProps> = ({
  isVisible,
  closeModal,
  claim,
  claimType,
}) => {
  const { data: claims } = useSelector((state: RootState) => state.claims)
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false)
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(claim)
  const [patientClaims, setPatientClaims] = useState<Claim[]>([])
  const [trackingTransaction, setTrackingTransaction] = useState<any>()

  useEffect(() => {
    setPatientClaims(
      filteredClaimsData(claims, undefined, undefined, claim.patientId),
    )
  }, [claim])

  useEffect(() => {
    if (selectedClaim) {
      const claimData = (claims as any).claims.find(
        (c: any) => c.claim_id === selectedClaim.id,
      )
      const claimStatus = (claimData.claimstatus as any[]).find(
        (x) => x.claim_id === selectedClaim.id,
      )
      if (claimStatus) {
        const match = claimStatus.xml.match(
          /<ChequeNum[^>]*>([\w\-_]+)<\/ChequeNum>/,
        )
        const reference_number = match ? match[1] : ''
        const tracking = (claims as any).tracking.find(
          (x: any) => x.reference_number === reference_number,
        )
        if (tracking) {
          setTrackingTransaction(tracking)
        } else {
          setTrackingTransaction(null)
        }
      }
    }
  }, [claims, selectedClaim])

  const handleClaimClick = (selected: Claim): void => {
    setSelectedClaim(selected)
  }

  const gridData =
    selectedClaim?.procedures.map((procedure) => {
      return {
        procedure_code: procedure.procedureCode,
        procedure_date: procedure.procedureDate,
        clinic_fee: procedure.clinic_fee,
        allowed_amount: procedure.allowed_amount,
        write_off: procedure.write_off,
        estimated_insurance_pay: procedure.estimated_insurance_pay,
        estimated_patient_resp: procedure.estimated_patient_resp,
        approved_amount: procedure.amountPaid,
        deductible: 0,
        copay: 0,
      }
    }) || []

  const columnsProcedures = useMemo(() => {
    return [
      {
        field: 'space',
        headerText: '',
        width: '16px',
      },
      {
        field: 'procedure_code',
        headerText: 'Code',
        width: '80px',
        footer: {
          template: () => <div>Total</div>,
        },
      },
      {
        field: 'procedure_date',
        headerText: 'Procedure date',
        width: '130px',
      },
      {
        field: 'clinic_fee',
        headerText: 'Clinic fee',
        width: '110px',
        template: (props: any) => {
          if (props.clinic_fee !== null && props.clinic_fee !== undefined) {
            return <span>${Number(props.clinic_fee).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'allowed_amount',
        headerText: 'Allowed amt.',
        width: '110px',
        template: (props: any) => {
          if (
            props.allowed_amount !== null &&
            props.allowed_amount !== undefined
          ) {
            return <span>${Number(props.allowed_amount).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'write_off',
        headerText: 'Write-off',
        width: '110px',
        template: (props: any) => {
          if (props.write_off !== null && props.write_off !== undefined) {
            return <span>${Number(props.write_off).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'estimated_insurance_pay',
        headerText: 'Est. ins. pay',
        width: '110px',
        template: (props: any) => {
          if (
            props.estimated_insurance_pay !== null &&
            props.estimated_insurance_pay !== undefined
          ) {
            return (
              <span>${Number(props.estimated_insurance_pay).toFixed(2)}</span>
            )
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'approved_amount',
        headerText: 'Approved ins.',
        width: '115px',
        template: (props: any) => {
          if (
            props.approved_amount !== null &&
            props.approved_amount !== undefined
          ) {
            return <span>${Number(props.approved_amount).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'deductible',
        headerText: 'Deductible',
        width: '110px',
        template: (props: any) => {
          if (props.deductible !== null && props.deductible !== undefined) {
            return <span>${Number(props.deductible).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'copay',
        headerText: 'Copay',
        width: 'auto',
        template: (props: any) => {
          if (props.copay !== null && props.copay !== undefined) {
            return <span>${Number(props.copay).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'estimated_patient_resp',
        headerText: 'Patient res.',
        width: '110px',
        template: (props: any) => {
          if (
            props.estimated_patient_resp !== null &&
            props.estimated_patient_resp !== undefined
          ) {
            return (
              <span>${Number(props.estimated_patient_resp).toFixed(2)}</span>
            )
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
    ].map((column) => ({
      ...column,
    }))
  }, [selectedClaim?.procedures])

  return (
    <Modal
      show={isVisible}
      onHide={closeModal}
      dialogClassName="modal-xxl"
      backdrop="static"
      className="view-claim-info-modal"
    >
      <div style={{ position: 'relative' }}>
        {isMemoModalOpen && <div className="modal-dark-overlay"></div>}

        <Modal.Header closeButton>
          <Modal.Title>Claim info</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col>
              <MessageComponent
                id="msg_info"
                content={
                  mapClaimStatus(selectedClaim?.status || '').description || ''
                }
                severity="Info"
                style={
                  CLAIMS_STATUS_STYLES[
                    selectedClaim?.generalStatus as unknown as
                      | StatusTypes.Approved
                      | StatusTypes.Rejected
                      | StatusTypes.UnderReview
                  ]
                }
              ></MessageComponent>
            </Col>
            <Col>
              <div className="d-flex justify-content-end mb-2">
                <Button
                  size="md"
                  className="button-secondary me-2"
                  onClick={() => setIsMemoModalOpen(true)}
                  disabled={true}
                >
                  View ERA
                </Button>
                <Button
                  size="md"
                  className="button-secondary"
                  onClick={() => setIsMemoModalOpen(true)}
                >
                  Memo
                </Button>
              </div>
            </Col>
          </Row>
          <Row className="gx-2">
            {/* Claim History Section */}
            <Col
              xs="auto"
              style={{
                border: '1px solid var(--blue-200)',
                padding: '0',
                borderRadius: '8px',
              }}
            >
              <div className="view-claim-info-section">
                <div className="view-claim-info-header">
                  <div className="view-claim-info-header-title">
                    Claim history
                  </div>
                  <div className="view-claim-info-count">
                    {`${patientClaims.filter((c) => c.claimType === claimType).length} ${patientClaims.filter((c) => c.claimType === claimType).length === 1 ? 'Claim' : 'Claims'}`}
                  </div>
                </div>
                <div className="view-claim-info-list">
                  {patientClaims
                    .filter((c) => c.claimType === claimType)
                    .map((c) => (
                      <div
                        key={c.id}
                        className={`view-claim-info-item ${selectedClaim?.id === c.id ? 'active' : ''}`}
                        onClick={() => handleClaimClick(c)}
                      >
                        <div className="view-claim-info-item-content-left">
                          <div className="view-claim-info-item-id">
                            {c.providerClaimId}
                          </div>
                          <div className="view-claim-info-item-date">
                            {new Date(c.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="view-claim-info-item-content-right">
                          <div className="view-claim-info-item-amount">
                            ${Number(c.claimAmount).toFixed(2)}
                          </div>
                          <div className="view-claim-info-item-status">
                            <StatusBadge
                              status={c.generalStatus as unknown as StatusTypes}
                              type="claims"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </Col>

            <Col>
              <div className="view-posting-grid-container grid-procedures-container">
                <ClaimPostingDetails
                  patientName={selectedClaim?.patientName || ''}
                  payerCarrier={selectedClaim?.insuranceCarrier || ''}
                  subscriber={selectedClaim?.subscriberName || ''}
                  provider={selectedClaim?.providerName || ''}
                  entityType={selectedClaim?.entityType || 'Individual'}
                  claimId={selectedClaim?.providerClaimId || ''}
                  claimStatus={
                    selectedClaim?.generalStatus as unknown as StatusTypes
                  }
                  submittedAmount={selectedClaim?.claimAmount || ''}
                  itemType={selectedClaim?.claimType || ''}
                  dateSent={
                    selectedClaim?.createdAt
                      ? new Date(selectedClaim.createdAt).toLocaleDateString()
                      : ''
                  }
                  checkNo={trackingTransaction?.reference_number || 'N/A'}
                  checkStatus={
                    trackingTransaction
                      ? getTrackingCheckStatus(trackingTransaction)
                      : 'N/A'
                  }
                  paidAmount={
                    trackingTransaction
                      ? '$' +
                        Math.abs(Number(trackingTransaction?.amount)).toFixed(2)
                      : 'N/A'
                  }
                  modeOfPayment={trackingTransaction?.payment_method || 'N/A'}
                  paymentDate={
                    trackingTransaction?.date
                      ? new Date(trackingTransaction?.date).toLocaleDateString()
                      : 'N/A'
                  }
                />

                <DataTreeGridComponent
                  data={gridData}
                  columns={columnsProcedures}
                  idMapping="id"
                  parentIdMapping="parentId"
                  treeColumnIndex={1}
                  rowHeight={36}
                  allowPaging={true}
                  allowTextWrap={true}
                  pageSize={10}
                  hasFooter={true}
                  footerData={columnsProcedures
                    .filter((col) => col.footer)
                    .map((col) => ({
                      field: col.field,
                      template: col.footer?.template ?? (() => <></>),
                    }))}
                />
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button size="md" className="button-secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </div>

      {isMemoModalOpen && (
        <ClaimMemoModal
          isVisible={isMemoModalOpen}
          closeModal={() => setIsMemoModalOpen(false)}
          onRestoreAfterNoteOverlay={() => setIsMemoModalOpen(true)}
          claimId={claim.id}
          readOnly={true}
          claim={claim}
        />
      )}
    </Modal>
  )
}

export default ViewClaimPostingInfo
